import { User } from '@auth0/auth0-react';
import { ApiService } from '../api/ApiService';
import { API_CONFIG } from '../api/config';

// Utilise la base URL depuis la config centralisée
const AUTH_API_URL = `${API_CONFIG.baseUrl}/auth`;

/**
 * Liste des clés localStorage à nettoyer lors de la déconnexion
 * Inclut les stores Zustand persistés et autres données utilisateur
 */
const STORAGE_KEYS_TO_CLEAR = [
  'token',
  'auth_token',
  'wanzo-auth-storage',
  'chat-storage',
  'fiscal-year-storage',
  'currency-storage',
  'business-unit-storage',
  'accounting-storage',
  'app-settings',
  'calculator-operations',
  'journal-entries',
];

class AuthService {
  async refreshTokenFromAuth0(
    user: User,
    accessToken: string,
    idToken: string,
    refreshToken: string | undefined,
    expiresIn: number
  ) {
    try {
      const response = await ApiService.post(`${AUTH_API_URL}/refresh-token`, {
        user,
        access_token: accessToken,
        id_token: idToken,
        refresh_token: refreshToken,
        expires_in: expiresIn,
      });
      return response;
    } catch (error) {
      console.error('Error refreshing token with backend', error);
      throw error;
    }
  }

  async validateTokenWithBackend(token: string) {
    try {
      const response = await fetch(`${AUTH_API_URL}/validate-token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return { isValid: false };

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error validating token with backend', error);
      return { isValid: false };
    }
  }

  /**
   * Déconnexion complète de l'utilisateur
   * 1. Invalide la session côté backend
   * 2. Supprime tous les tokens et données locales
   * 3. Nettoie les stores Zustand persistés
   */
  async logout() {
    try {
      // 1. Invalider la session côté backend
      await ApiService.post(`${AUTH_API_URL}/invalidate-session`, {});
    } catch (error) {
      console.error('Error invalidating backend session', error);
      // On continue quand même avec le nettoyage local
    }
    
    // 2. Nettoyer toutes les données locales
    this.clearAllLocalData();
  }

  /**
   * Nettoie toutes les données utilisateur du localStorage
   * Appelé lors de la déconnexion pour une réinitialisation complète
   */
  clearAllLocalData() {
    console.log('🧹 Nettoyage complet des données locales...');
    
    // Supprimer les clés connues
    STORAGE_KEYS_TO_CLEAR.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Impossible de supprimer la clé ${key}:`, error);
      }
    });
    
    // Supprimer également les clés Auth0 potentielles
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('@@auth0') || 
        key.startsWith('auth0') ||
        key.includes('auth') ||
        key.includes('token')
      )) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Impossible de supprimer la clé ${key}:`, error);
      }
    });
    
    // Nettoyer sessionStorage aussi
    try {
      sessionStorage.clear();
    } catch (error) {
      console.warn('Impossible de nettoyer sessionStorage:', error);
    }
    
    console.log('✅ Données locales nettoyées');
  }
}

export const authService = new AuthService();
